import React, { createContext, useState, useEffect } from "react";

export const RemixBuilderContext = createContext();

// Once the complete state is shown we're navigating away — don't let late
// progress updates overwrite it back to the loading state.
export function applyRemixLoading(prev, loadingState) {
  if (prev.remix_loading?.complete === true && loadingState?.complete !== true) {
    return prev;
  }
  return {
    ...prev,
    remix_loading: loadingState,
  };
}

const CTA_TYPE_OPTIONS = [
  { value: "page", label: "Landing Page" },
  { value: "overlay", label: "Overlay Popup" },
  { value: "hook", label: "Hook Popup" },
  { value: "bar", label: "Sticky Bar" },
  { value: "inline", label: "Embed" },
];

function isValidSectionSpec(template) {
  if (!template || typeof template !== "object") return false;
  return (
    (template.id != null && template.id !== "") ||
    (template.tag_name != null && String(template.tag_name).trim() !== "")
  );
}

function buildInitialMatchedSections(sectionTemplates) {
  if (!Array.isArray(sectionTemplates) || sectionTemplates.length === 0) {
    return [];
  }
  const valid = sectionTemplates.filter(isValidSectionSpec);
  if (valid.length === 0) return [];
  return valid.map((template, index) => ({
    id: crypto.randomUUID(),
    position:
      Number.isFinite(parseInt(template.position, 10)) &&
      parseInt(template.position, 10) > 0
        ? parseInt(template.position, 10)
        : index + 1,
    step_position:
      Number.isFinite(parseInt(template.step_position, 10)) &&
      parseInt(template.step_position, 10) > 0
        ? parseInt(template.step_position, 10)
        : 1,
    selected: true,
    step_name:
      template.step_name != null && String(template.step_name).trim() !== ""
        ? String(template.step_name).trim()
        : undefined,
    tags: template.template_tags || [],
    remix_tone:
      template.remix_tone != null ? String(template.remix_tone).trim() : "",
    template: template,
    screenshot: template.screenshot,
  }));
}

function normalizeStepPosition(value, fallback = 1) {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function sanitizeStepName(value, stepPosition = null, options = {}) {
  const raw = String(value || "");
  if (raw.trim() === "") return "";

  const positionPattern =
    Number.isFinite(parseInt(stepPosition, 10)) && parseInt(stepPosition, 10) > 0
      ? new RegExp(`^(?:funnel\\s*)?step\\s*${parseInt(stepPosition, 10)}\\s*[-:]+\\s*`, "i")
      : /^((?:funnel\s*)?step\s*\d+\s*[-:]+\s*)/i;

  const normalized = raw.replace(positionPattern, "").replace(/^\s+/, "");
  if (options.preserveTrailingWhitespace === true) return normalized;
  return normalized.trim();
}

function normalizeSectionsForStep(templates, stepPosition, stepName = null) {
  if (!Array.isArray(templates) || templates.length === 0) return [];
  const valid = templates.filter(isValidSectionSpec);
  return valid.map((template, index) => ({
    id: crypto.randomUUID(),
    step_position: stepPosition,
    position: index + 1,
    selected: true,
    step_name:
      stepName != null && String(stepName).trim() !== ""
        ? String(stepName).trim()
        : undefined,
    tags: template.template_tags || [],
    remix_tone:
      template.remix_tone != null ? String(template.remix_tone).trim() : "",
    template: template,
    screenshot: template.screenshot,
  }));
}

function buildInitialMatchedSectionsWithSteps(
  sectionTemplates,
  initialFunnelSteps = []
) {
  if (Array.isArray(initialFunnelSteps) && initialFunnelSteps.length > 0) {
    const allStepSections = initialFunnelSteps.flatMap((funnelStep, index) => {
      const stepPosition = normalizeStepPosition(
        funnelStep?.step_position,
        index + 1
      );
      const templates = funnelStep?.section_templates || [];
      const stepName = funnelStep?.step_name;
      return normalizeSectionsForStep(templates, stepPosition, stepName);
    });
    return allStepSections;
  }

  const normalized = buildInitialMatchedSections(sectionTemplates);
  return normalized.map((section) => ({
    ...section,
    step_position: normalizeStepPosition(section.step_position, 1),
  }));
}

function uniqueSortedStepPositions(matchedSections = []) {
  const positions = new Set(
    matchedSections.map((section) => normalizeStepPosition(section.step_position))
  );
  if (positions.size === 0) return [1];
  return Array.from(positions).sort((a, b) => a - b);
}

function buildInitialStepNames(initialFunnelSteps = [], matchedSections = []) {
  const names = {};
  if (Array.isArray(initialFunnelSteps) && initialFunnelSteps.length > 0) {
    initialFunnelSteps.forEach((step, index) => {
      const stepPosition = normalizeStepPosition(step?.step_position, index + 1);
      const sanitized = sanitizeStepName(step?.step_name, stepPosition);
      // Keep step positions from preload even when name is blank.
      names[stepPosition] = sanitized;
    });
  }

  matchedSections.forEach((section) => {
    const stepPosition = normalizeStepPosition(section.step_position, 1);
    if (!names[stepPosition] && section?.step_name) {
      names[stepPosition] = sanitizeStepName(section.step_name, stepPosition);
    }
  });

  return names;
}

function stepPositionsFromState(matchedSections = [], funnelStepNames = {}) {
  const positions = new Set(
    (matchedSections || []).map((section) =>
      normalizeStepPosition(section.step_position, 1)
    )
  );
  Object.keys(funnelStepNames || {}).forEach((stepPosition) => {
    positions.add(normalizeStepPosition(stepPosition, 1));
  });
  if (positions.size === 0) return [1];
  return Array.from(positions).sort((a, b) => a - b);
}

function canAddFunnelStepFromState(matchedSections = [], funnelStepNames = {}) {
  const stepPositions = stepPositionsFromState(matchedSections, funnelStepNames);
  const sectionCountsByStep = {};

  (matchedSections || []).forEach((section) => {
    const stepPosition = normalizeStepPosition(section.step_position, 1);
    sectionCountsByStep[stepPosition] = (sectionCountsByStep[stepPosition] || 0) + 1;
  });

  return stepPositions.every((stepPosition) => (sectionCountsByStep[stepPosition] || 0) > 0);
}

function reindexAfterRemovingStep(
  matchedSections = [],
  funnelStepNames = {},
  activeStep = 1,
  stepToRemove = 1
) {
  const removedStep = normalizeStepPosition(stepToRemove, 1);
  const filteredSections = (matchedSections || []).filter(
    (section) => normalizeStepPosition(section.step_position, 1) !== removedStep
  );
  const remainingOldPositions = stepPositionsFromState(
    filteredSections,
    Object.fromEntries(
      Object.entries(funnelStepNames || {}).filter(
        ([stepPosition]) => normalizeStepPosition(stepPosition, 1) !== removedStep
      )
    )
  ).sort((a, b) => a - b);

  const normalizedRemainingOldPositions =
    remainingOldPositions.length > 0 ? remainingOldPositions : [1];
  const oldToNew = {};
  normalizedRemainingOldPositions.forEach((oldPosition, index) => {
    oldToNew[oldPosition] = index + 1;
  });

  const sectionOrderCounter = {};
  const remappedSections = filteredSections
    .map((section) => ({
      ...section,
      step_position:
        oldToNew[normalizeStepPosition(section.step_position, 1)] || 1,
    }))
    .sort((a, b) => {
      const stepA = normalizeStepPosition(a.step_position, 1);
      const stepB = normalizeStepPosition(b.step_position, 1);
      if (stepA !== stepB) return stepA - stepB;
      return (a.position || 0) - (b.position || 0);
    })
    .map((section) => {
      const stepPosition = normalizeStepPosition(section.step_position, 1);
      sectionOrderCounter[stepPosition] = (sectionOrderCounter[stepPosition] || 0) + 1;
      return {
        ...section,
        position: sectionOrderCounter[stepPosition],
      };
    });

  const remappedNames = {};
  Object.entries(funnelStepNames || {}).forEach(([stepPosition, stepName]) => {
    const oldPosition = normalizeStepPosition(stepPosition, 1);
    if (oldPosition === removedStep) return;
    const newPosition = oldToNew[oldPosition];
    if (newPosition) remappedNames[newPosition] = stepName;
  });
  Object.values(oldToNew).forEach((newPosition) => {
    if (!(newPosition in remappedNames)) remappedNames[newPosition] = "";
  });

  const normalizedActiveStep = normalizeStepPosition(activeStep, 1);
  let nextActiveOldPosition = normalizedActiveStep;
  if (normalizedActiveStep === removedStep) {
    nextActiveOldPosition =
      normalizedRemainingOldPositions.find((position) => position > removedStep) ||
      normalizedRemainingOldPositions[normalizedRemainingOldPositions.length - 1];
  }
  const nextActiveStep = oldToNew[nextActiveOldPosition] || 1;

  return {
    matched_sections: remappedSections,
    funnel_step_names: remappedNames,
    active_funnel_step: nextActiveStep,
  };
}

const POPUP_CTA_TYPES = ["overlay", "hook"];

function resolveCtaType(initialCtaType, sectionTemplates = []) {
  if (!initialCtaType) return CTA_TYPE_OPTIONS[0];
  // Normalize "overlay,hook" (from playbook filter) to first valid option
  const preferred = String(initialCtaType)
    .split(",")
    .map((s) => s.trim())
    .find((v) => CTA_TYPE_OPTIONS.some((o) => o.value === v));
  const preferredValue = preferred || CTA_TYPE_OPTIONS[0].value;
  const templateCtaTypes = sectionTemplates
    .map((t) => t?.cta_type || t?.template?.cta_type)
    .filter(Boolean);
  const hasPopupTemplate = templateCtaTypes.some((t) =>
    POPUP_CTA_TYPES.includes(t)
  );
  const userWantedPopup = POPUP_CTA_TYPES.includes(preferredValue);
  // If user wanted popup but playbook has no popup templates, pre-select overlay
  const value =
    userWantedPopup && !hasPopupTemplate ? "overlay" : preferredValue;
  return CTA_TYPE_OPTIONS.find((o) => o.value === value) || CTA_TYPE_OPTIONS[0];
}

export const RemixBuilderProvider = (props) => {
  const initialMatchedSections = buildInitialMatchedSectionsWithSteps(
    props.initialMatchedSections || props.initialSectionTemplates,
    props.initialFunnelSteps
  );
  const initialRemixPrompt = props.initialRemixPrompt || "";
  const hasInitialTemplate = initialMatchedSections.length > 0;
  const hasInitialTemplateInput =
    (Array.isArray(props.initialSectionTemplates) &&
      props.initialSectionTemplates.length > 0) ||
    (Array.isArray(props.initialFunnelSteps) &&
      props.initialFunnelSteps.length > 0);
  const initialStepPositions = uniqueSortedStepPositions(initialMatchedSections);
  const initialStepNames = buildInitialStepNames(
    props.initialFunnelSteps,
    initialMatchedSections
  );

  const defaultOptions = props.options || {};
  const baseOptions = {
    remix_all_text: false,
    remix_tone: "",
    remix_asset_urls: [],
    excluded_element_types: [],
    ...defaultOptions,
  };
  const optionsWithRemix =
    hasInitialTemplate && initialRemixPrompt
      ? {
          ...baseOptions,
          remix_all_text: true,
          remix_tone: initialRemixPrompt,
        }
      : baseOptions;

  const isVariantFlow = props.type === "variant";

  const baseRemixSettings = {
    styles: props.styles || {},
    options: optionsWithRemix,
  };

  const sectionTemplatesForCtaType =
    props.initialSectionTemplates ||
    (initialMatchedSections.length > 0
      ? initialMatchedSections.map((s) => s.template).filter(Boolean)
      : []);

  const remixSettings = isVariantFlow
    ? { ...baseRemixSettings, variant_name: "" }
    : {
        ...baseRemixSettings,
        cta_type: resolveCtaType(
          props.initialCtaType,
          sectionTemplatesForCtaType
        ),
        campaign_name: props.initialCampaignName || "",
      };

  let context = {
    current_user: props.current_user,
    website: props.website,
    cta: props.cta,
    subscription: props.subscription,
    account: props.account,
    currentMonthYear: moment().format("MMMM YYYY"),
    environment: props.environment,
    filestack_api_key: props.FILESTACK_API_KEY,
    uploaded_sections: [],
    matched_sections: hasInitialTemplate ? initialMatchedSections : [],
    active_funnel_step: initialStepPositions[0] || 1,
    funnel_step_names: initialStepNames,
    section_templates: [],
    // When preload metadata exists but template matching returns empty, keep the
    // builder on a visible step instead of a blank "step 0" modal.
    current_step: hasInitialTemplate || hasInitialTemplateInput ? 2 : 0,
    from_scratch: hasInitialTemplate,
    remix_settings: remixSettings,
    settings: {
      max_section_remixes: 50,
      used_section_remixes: hasInitialTemplate
        ? initialMatchedSections.length
        : 0,
    },
    onSubmit: props.onSubmit,
    remix_mappings: props.remix_mappings,
    remix_loading: null,
    detected_asset_theme: props.detectedAssetTheme || null,
    detected_theme: props.detectedAssetTheme || null,
  };

  const [contextData, setContextData] = useState(context);

  let builder = {
    toggleSelectSection: function (section, targetKey = "uploaded_sections") {
      let updatedBuilder = { ...contextData };

      updatedBuilder[targetKey] = updatedBuilder[targetKey].map(
        (uploadedSection) => {
          if (uploadedSection.id === section.id) {
            if (uploadedSection.selected) {
              uploadedSection.selected = false;
            } else if (
              updatedBuilder.settings.used_section_remixes <
              updatedBuilder.settings.max_section_remixes
            ) {
              uploadedSection.selected = true;
            }
          }
          return uploadedSection;
        }
      );

      setContextData(updatedBuilder);
    },

    toggleAllUploadedSections: function () {
      let updatedBuilder = { ...contextData };

      if (
        updatedBuilder.uploaded_sections.some((section) => section.selected)
      ) {
        updatedBuilder.uploaded_sections = updatedBuilder.uploaded_sections.map(
          (section) => ({ ...section, selected: false })
        );
      } else {
        const limitToUse =
          updatedBuilder.settings.max_section_remixes -
          updatedBuilder.settings.used_section_remixes;

        updatedBuilder.uploaded_sections = updatedBuilder.uploaded_sections.map(
          (section, index) => ({
            ...section,
            selected: index < limitToUse,
          })
        );
      }

      setContextData(updatedBuilder);
    },

    updateSectionPosition: function (section, position) {
      let updatedBuilder = { ...contextData };

      updatedBuilder.uploaded_sections = updatedBuilder.uploaded_sections.map(
        (uploadedSection) => {
          if (uploadedSection.id === section.id) {
            return {
              ...uploadedSection,
              position: position,
            };
          }
          return uploadedSection;
        }
      );
      setContextData(updatedBuilder);
    },

    updateRemixSettings: function (key, value) {
      setContextData((prevContextData) => {
        const newRemixSettings = {
          ...prevContextData.remix_settings,
          [key]: value,
        };

        return {
          ...prevContextData,
          remix_settings: newRemixSettings,
        };
      });
    },

    addNewMatchedSection: function (sectionTemplate = null, stepPositionOverride = null) {
      let updatedBuilder = { ...contextData };
      const stepPosition = normalizeStepPosition(
        stepPositionOverride || updatedBuilder.active_funnel_step,
        1
      );
      const sectionsForStep = (updatedBuilder.matched_sections || []).filter(
        (section) => normalizeStepPosition(section.step_position, 1) === stepPosition
      );
      const nextPosition = sectionsForStep.length + 1;

      let newSection = {
        id: crypto.randomUUID(),
        step_position: stepPosition,
        position: nextPosition,
        selected: true,
        step_name: updatedBuilder.funnel_step_names?.[stepPosition],
        remix_tone: "",
        tags: [],
      };

      if (sectionTemplate !== null) {
        newSection.template = sectionTemplate;
        newSection.screenshot = sectionTemplate.screenshot;
      } else {
        newSection.template = {
          id: 0,
          name: "Create From Scratch",
          description: "Create a new section from scratch",
          image_bytes:
            "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=",
        };
      }

      setContextData((prevContextData) => ({
        ...prevContextData,
        matched_sections: [...prevContextData.matched_sections, newSection],
      }));
    },

    setActiveFunnelStep: function (stepPosition) {
      setContextData((prevContextData) => ({
        ...prevContextData,
        active_funnel_step: normalizeStepPosition(stepPosition, 1),
      }));
    },

    addFunnelStep: function () {
      setContextData((prevContextData) => {
        if (
          !canAddFunnelStepFromState(
            prevContextData.matched_sections,
            prevContextData.funnel_step_names
          )
        ) {
          return prevContextData;
        }

        const existing = stepPositionsFromState(
          prevContextData.matched_sections,
          prevContextData.funnel_step_names
        );
        const nextStep =
          existing.length > 0 ? existing[existing.length - 1] + 1 : 1;
        return {
          ...prevContextData,
          active_funnel_step: nextStep,
          funnel_step_names: {
            ...(prevContextData.funnel_step_names || {}),
            [nextStep]: "",
          },
        };
      });
    },

    canAddFunnelStep: function () {
      return canAddFunnelStepFromState(
        contextData.matched_sections,
        contextData.funnel_step_names
      );
    },

    removeFunnelStep: function (stepPosition) {
      setContextData((prevContextData) => {
        const allStepPositions = stepPositionsFromState(
          prevContextData.matched_sections,
          prevContextData.funnel_step_names
        );
        if (allStepPositions.length <= 1) return prevContextData;

        const normalizedStepPosition = normalizeStepPosition(stepPosition, 1);
        if (!allStepPositions.includes(normalizedStepPosition)) {
          return prevContextData;
        }

        const reindexed = reindexAfterRemovingStep(
          prevContextData.matched_sections,
          prevContextData.funnel_step_names,
          prevContextData.active_funnel_step,
          normalizedStepPosition
        );

        return {
          ...prevContextData,
          matched_sections: reindexed.matched_sections,
          funnel_step_names: reindexed.funnel_step_names,
          active_funnel_step: reindexed.active_funnel_step,
        };
      });
    },

    updateFunnelStepName: function (stepPosition, stepName) {
      const normalized = normalizeStepPosition(stepPosition, 1);
      const sanitized = sanitizeStepName(stepName, normalized, {
        preserveTrailingWhitespace: true,
      });
      setContextData((prevContextData) => ({
        ...prevContextData,
        funnel_step_names: {
          ...(prevContextData.funnel_step_names || {}),
          [normalized]: sanitized,
        },
      }));
    },

    getFunnelStepName: function (stepPosition) {
      const normalized = normalizeStepPosition(stepPosition, 1);
      const name = sanitizeStepName(contextData.funnel_step_names?.[normalized], normalized);
      if (name) return name;
      return `Step ${normalized}`;
    },

    changeMatchedSectionTemplate: function (
      section,
      sectionTemplate,
      preserveMatchedTemplates = false
    ) {
      setContextData((prevContextData) => ({
        ...prevContextData,
        matched_sections: prevContextData.matched_sections.map(
          (matchedSection) => {
            if (matchedSection.id === section.id) {
              if (preserveMatchedTemplates && matchedSection.matchedTemplates) {
                const newIndex = matchedSection.matchedTemplates.findIndex(
                  (t) => t.id === sectionTemplate.id
                );
                return {
                  ...matchedSection,
                  template: sectionTemplate,
                  remix_tone:
                    sectionTemplate?.remix_tone != null
                      ? String(sectionTemplate.remix_tone).trim()
                      : matchedSection.remix_tone || "",
                  currentTemplateIndex: newIndex >= 0 ? newIndex : 0,
                };
              } else {
                return {
                  ...matchedSection,
                  template: sectionTemplate,
                  remix_tone:
                    sectionTemplate?.remix_tone != null
                      ? String(sectionTemplate.remix_tone).trim()
                      : matchedSection.remix_tone || "",
                };
              }
            }
            return matchedSection;
          }
        ),
      }));
    },

    updateMatchedSectionRemixTone: function (section, remixTone) {
      setContextData((prevContextData) => ({
        ...prevContextData,
        matched_sections: prevContextData.matched_sections.map(
          (matchedSection) => {
            if (matchedSection.id !== section.id) return matchedSection;

            return {
              ...matchedSection,
              remix_tone: remixTone,
            };
          }
        ),
      }));
    },

    tryNextTemplate: function (section) {
      setContextData((prevContextData) => ({
        ...prevContextData,
        matched_sections: prevContextData.matched_sections.map(
          (matchedSection) => {
            if (
              matchedSection.id === section.id &&
              matchedSection.matchedTemplates
            ) {
              const currentIndex = matchedSection.currentTemplateIndex || 0;
              const nextIndex = currentIndex + 1;

              if (nextIndex < matchedSection.matchedTemplates.length) {
                return {
                  ...matchedSection,
                  template: matchedSection.matchedTemplates[nextIndex],
                  currentTemplateIndex: nextIndex,
                };
              }
            }
            return matchedSection;
          }
        ),
      }));
    },

    goToStep: function (step) {
      let updatedBuilder = { ...contextData };
      updatedBuilder.current_step = step;
      setContextData(updatedBuilder);
    },

    goToPreviousStep: function () {
      let updatedBuilder = { ...contextData };
      updatedBuilder.current_step = updatedBuilder.current_step - 1;
      setContextData(updatedBuilder);
    },

    goToNextStep: function (newContextData = null) {
      let updatedBuilder = newContextData || { ...contextData };
      updatedBuilder.current_step = updatedBuilder.current_step + 1;
      setContextData(updatedBuilder);
    },

    removeSectionTag: function (section, tagName) {
      let updatedBuilder = { ...contextData };

      updatedBuilder.uploaded_sections = updatedBuilder.uploaded_sections.map(
        (uploadedSection) => {
          if (uploadedSection.id === section.id) {
            uploadedSection.tags = uploadedSection.tags.filter(
              (tag) => tag.name !== tagName
            );
          }

          return uploadedSection;
        }
      );

      setContextData(updatedBuilder);
    },

    setRemixLoading: function (loadingState) {
      setContextData((prev) => applyRemixLoading(prev, loadingState));
    },
  };

  useEffect(() => {
    let updatedBuilder = { ...contextData };

    if (updatedBuilder.uploaded_sections) {
      updatedBuilder.settings.used_section_remixes =
        updatedBuilder.uploaded_sections.filter(
          (section) => section.selected
        ).length;

      setContextData(updatedBuilder);
    }
  }, [contextData.uploaded_sections]);

  return (
    <RemixBuilderContext.Provider
      value={[contextData, builder, setContextData]}
    >
      {props.children}
    </RemixBuilderContext.Provider>
  );
};
